import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { SignedAvatarImage } from '@/components/shared/SignedAvatarImage';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sparkles, Loader2, RefreshCw, Users, Calendar } from 'lucide-react';
import { format } from 'date-fns';
import { useToast } from '@/hooks/use-toast';
import { useState } from 'react';

interface MatchupRow {
  id: string;
  ai_reason: string;
  created_at: string;
  user_id_1: string;
  user_id_2: string;
  round_id: string;
}

interface RoundRow {
  id: string;
  week_of: string;
  profile_count: number;
  pair_count: number;
  status: string;
  created_at: string;
}

interface ProfileMap {
  [userId: string]: {
    full_name: string;
    avatar_url: string | null;
    company: string | null;
    role: string | null;
    city_cohort: string;
  };
}

function useAdminMatchups() {
  return useQuery({
    queryKey: ['admin-matchups'],
    queryFn: async () => {
      // Fetch all rounds
      const { data: rounds, error: roundsError } = await supabase
        .from('matchup_rounds')
        .select('*')
        .order('week_of', { ascending: false });

      if (roundsError) throw roundsError;

      // Fetch all matchups
      const { data: matchups, error: matchupsError } = await supabase
        .from('matchups')
        .select('*')
        .order('created_at', { ascending: false });

      if (matchupsError) throw matchupsError;

      // Get all user IDs from matchups
      const userIds = new Set<string>();
      matchups?.forEach((m) => {
        userIds.add(m.user_id_1);
        userIds.add(m.user_id_2);
      });

      let profiles: ProfileMap = {};
      if (userIds.size > 0) {
        const { data: profileData } = await supabase
          .from('profiles')
          .select('user_id, full_name, avatar_url, company, role, city_cohort')
          .in('user_id', Array.from(userIds));

        if (profileData) {
          profiles = Object.fromEntries(profileData.map((p) => [p.user_id, p]));
        }
      }

      return {
        rounds: (rounds || []) as RoundRow[],
        matchups: (matchups || []) as MatchupRow[],
        profiles,
      };
    },
  });
}

export default function AdminMatchups() {
  const { data, isLoading, refetch } = useAdminMatchups();
  const { toast } = useToast();
  const [triggering, setTriggering] = useState(false);
  const [matchesPerMember, setMatchesPerMember] = useState('1');

  const handleTrigger = async () => {
    setTriggering(true);
    try {
      const { data: result, error } = await supabase.functions.invoke('generate-matchups', {
        body: { matches_per_member: parseInt(matchesPerMember) },
      });

      if (error) throw error;

      if (result.success) {
        toast({ title: 'Matchups generated', description: `${result.pairs} pairs from ${result.profiles} profiles` });
        refetch();
      } else {
        toast({ title: 'Matchup result', description: result.message || result.error, variant: result.error ? 'destructive' : 'default' });
      }
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setTriggering(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const { rounds = [], matchups = [], profiles = {} } = data || {};

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-bold text-foreground">Matchups</h1>
          <p className="text-muted-foreground text-sm">
            View all weekly AI matchups and trigger new rounds
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={matchesPerMember} onValueChange={setMatchesPerMember}>
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">1 match/member</SelectItem>
              <SelectItem value="2">2 matches/member</SelectItem>
              <SelectItem value="3">3 matches/member</SelectItem>
            </SelectContent>
          </Select>
          <Button
            onClick={handleTrigger}
            disabled={triggering}
            className="btn-qbn-primary"
          >
            {triggering ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Generate Now
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Rounds</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{rounds.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Pairs Made</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{matchups.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Unique Members Matched</CardTitle>
            <Sparkles className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {new Set(matchups.flatMap((m) => [m.user_id_1, m.user_id_2])).size}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Rounds */}
      {rounds.map((round) => {
        const roundMatchups = matchups.filter((m) => m.round_id === round.id);

        return (
          <Card key={round.id}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CardTitle className="text-lg">
                    Week of {format(new Date(round.week_of), 'MMM d, yyyy')}
                  </CardTitle>
                  <Badge variant={round.status === 'completed' ? 'default' : 'destructive'}>
                    {round.status}
                  </Badge>
                </div>
                <span className="text-sm text-muted-foreground">
                  {round.pair_count} pairs / {round.profile_count} profiles
                </span>
              </div>
            </CardHeader>
            <CardContent>
              {roundMatchups.length === 0 ? (
                <p className="text-sm text-muted-foreground">No matchups in this round.</p>
              ) : (
                <div className="space-y-3">
                  {roundMatchups.map((matchup) => {
                    const p1 = profiles[matchup.user_id_1];
                    const p2 = profiles[matchup.user_id_2];

                    return (
                      <div
                        key={matchup.id}
                        className="flex items-start gap-4 p-4 bg-muted/30 rounded-lg"
                      >
                        {/* Person 1 */}
                        <div className="flex items-center gap-2 min-w-[180px]">
                          <Avatar className="h-9 w-9">
                            <SignedAvatarImage src={p1?.avatar_url || undefined} />
                            <AvatarFallback className="bg-primary/10 text-primary text-sm">
                              {p1?.full_name?.charAt(0) || '?'}
                            </AvatarFallback>
                          </Avatar>
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate">{p1?.full_name || 'Unknown'}</p>
                            <p className="text-xs text-muted-foreground truncate">
                              {[p1?.role, p1?.company].filter(Boolean).join(' at ') || p1?.city_cohort}
                            </p>
                          </div>
                        </div>

                        {/* Arrow */}
                        <div className="flex items-center pt-2">
                          <Sparkles className="h-4 w-4 text-primary" />
                        </div>

                        {/* Person 2 */}
                        <div className="flex items-center gap-2 min-w-[180px]">
                          <Avatar className="h-9 w-9">
                            <SignedAvatarImage src={p2?.avatar_url || undefined} />
                            <AvatarFallback className="bg-primary/10 text-primary text-sm">
                              {p2?.full_name?.charAt(0) || '?'}
                            </AvatarFallback>
                          </Avatar>
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate">{p2?.full_name || 'Unknown'}</p>
                            <p className="text-xs text-muted-foreground truncate">
                              {[p2?.role, p2?.company].filter(Boolean).join(' at ') || p2?.city_cohort}
                            </p>
                          </div>
                        </div>

                        {/* AI Reason */}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-muted-foreground leading-relaxed">
                            {matchup.ai_reason}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}

      {rounds.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <Sparkles className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
            <p className="text-muted-foreground">No matchup rounds yet. Click "Generate Now" to create the first round.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
